import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { CalendarIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, TrashIcon, XIcon } from 'lucide-react';
// Mock data for availability
const mockAvailabilityData = {
  preferences: {
    maxDaysPerWeek: 4,
    preferredLocations: ['Rotterdam', 'Utrecht'],
    preferNotMonday: true,
    preferredTrainingTypes: ['Sliptraining Basis', 'Defensief Rijden']
  },
  // Availability for the next 30 days
  availability: Array.from({
    length: 30
  }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() + i);
    // Randomly set availability
    const isAvailable = Math.random() > 0.3;
    return {
      date,
      available: isAvailable,
      morningAvailable: isAvailable ? Math.random() > 0.3 : false,
      afternoonAvailable: isAvailable ? Math.random() > 0.3 : false,
      eveningAvailable: isAvailable ? Math.random() > 0.3 : false,
      note: isAvailable ? '' : Math.random() > 0.7 ? 'Persoonlijke afspraak' : ''
    };
  }),
  // Scheduled trainings
  scheduledTrainings: [{
    id: 1,
    date: new Date(new Date().setDate(new Date().getDate() + 3)),
    startTime: '09:00',
    endTime: '12:00',
    type: 'Sliptraining Basis',
    location: 'Rotterdam'
  }, {
    id: 2,
    date: new Date(new Date().setDate(new Date().getDate() + 5)),
    startTime: '13:30',
    endTime: '16:30',
    type: 'Defensief Rijden',
    location: 'Utrecht'
  }, {
    id: 3,
    date: new Date(new Date().setDate(new Date().getDate() + 10)),
    startTime: '10:00',
    endTime: '14:00',
    type: 'Code 95 Training',
    location: 'Amsterdam'
  }]
};
export function MyAvailability() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availabilityData, setAvailabilityData] = useState(mockAvailabilityData);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [editingPreferences, setEditingPreferences] = useState({
    ...mockAvailabilityData.preferences
  });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDateModal, setShowDateModal] = useState(false);
  const [editingDateAvailability, setEditingDateAvailability] = useState(null);
  // Get days in month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };
  // Get first day of month (0 = Sunday, 1 = Monday, etc.)
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };
  // Format date as YYYY-MM-DD
  const formatDateKey = date => {
    return date.toISOString().split('T')[0];
  };
  // Get availability for a specific date
  const getAvailabilityForDate = date => {
    const dateStr = formatDateKey(date);
    return availabilityData.availability.find(a => formatDateKey(new Date(a.date)) === dateStr) || {
      date,
      available: false,
      morningAvailable: false,
      afternoonAvailable: false,
      eveningAvailable: false,
      note: ''
    };
  };
  // Check if date has scheduled training
  const hasScheduledTraining = date => {
    const dateStr = formatDateKey(date);
    return availabilityData.scheduledTrainings.some(t => formatDateKey(new Date(t.date)) === dateStr);
  };
  // Get scheduled trainings for date
  const getTrainingsForDate = date => {
    const dateStr = formatDateKey(date);
    return availabilityData.scheduledTrainings.filter(t => formatDateKey(new Date(t.date)) === dateStr);
  };
  // Handle next month
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  // Handle previous month
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  // Handle saving preferences
  const handleSavePreferences = () => {
    setAvailabilityData({
      ...availabilityData,
      preferences: editingPreferences
    });
    setShowPreferencesModal(false);
    setSuccessMessage('Voorkeuren succesvol opgeslagen');
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  };
  // Handle date click
  const handleDateClick = date => {
    // Don't allow editing past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return;
    setSelectedDate(date);
    setEditingDateAvailability(getAvailabilityForDate(date));
    setShowDateModal(true);
  };
  // Handle saving date availability
  const handleSaveDateAvailability = () => {
    setAvailabilityData({
      ...availabilityData,
      availability: availabilityData.availability.map(a => formatDateKey(new Date(a.date)) === formatDateKey(selectedDate) ? editingDateAvailability : a)
    });
    setShowDateModal(false);
    setSuccessMessage('Beschikbaarheid succesvol opgeslagen');
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  };
  // Render calendar
  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);
    // Adjust for Monday as first day of week
    const firstDayAdjusted = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    const days = [];
    // Add empty cells for days before first day of month
    for (let i = 0; i < firstDayAdjusted; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 bg-gray-50" data-id="element-1291"></div>);
    }
    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isToday = new Date().toDateString() === date.toDateString();
      const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
      const availability = getAvailabilityForDate(date);
      const hasTraining = hasScheduledTraining(date);
      days.push(<div key={day} className={`h-24 border border-gray-200 p-1 ${isPast ? 'bg-gray-50' : 'bg-white'} ${isToday ? 'border-[#B61C1C]' : ''}`} onClick={() => !isPast && handleDateClick(date)} data-id="element-1292">
          <div className="flex justify-between items-center" data-id="element-1293">
            <span className={`text-sm font-medium ${isToday ? 'text-[#B61C1C]' : isPast ? 'text-gray-400' : 'text-gray-900'}`} data-id="element-1294">
              {day}
            </span>
            {availability.available && <span className="flex space-x-1" data-id="element-1295">
                {availability.morningAvailable && <span className="w-2 h-2 bg-green-500 rounded-full" data-id="element-1296"></span>}
                {availability.afternoonAvailable && <span className="w-2 h-2 bg-green-500 rounded-full" data-id="element-1297"></span>}
                {availability.eveningAvailable && <span className="w-2 h-2 bg-green-500 rounded-full" data-id="element-1298"></span>}
              </span>}
            {!availability.available && !isPast && <span className="w-2 h-2 bg-red-500 rounded-full" data-id="element-1299"></span>}
          </div>
          <div className="mt-1 space-y-1" data-id="element-1300">
            {hasTraining && <div className="text-xs bg-blue-100 text-blue-800 px-1 py-0.5 rounded truncate" data-id="element-1301">
                Training
              </div>}
            {availability.note && <div className="text-xs text-gray-500 truncate" data-id="element-1302">
                {availability.note}
              </div>}
          </div>
        </div>);
    }
    return days;
  };
  return <div className="space-y-6" data-id="element-1303">
      <div className="flex justify-between items-center" data-id="element-1304">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1305">
          Mijn Beschikbaarheid
        </h1>
        <div className="flex space-x-2" data-id="element-1306">
          <Button variant="outline" size="sm" onClick={() => setShowPreferencesModal(true)} data-id="element-1307">
            <CalendarIcon className="h-4 w-4 mr-1" data-id="element-1308" />
            Voorkeuren
          </Button>
        </div>
      </div>

      {/* Success message */}
      {showSuccessMessage && <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50 shadow-md flex items-center" data-id="element-1309">
          <CheckIcon className="h-5 w-5 mr-2" data-id="element-1310" />
          <span data-id="element-1311">{successMessage}</span>
        </div>}

      {/* Calendar */}
      <Card data-id="element-1312">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1313">
          <div className="flex space-x-2" data-id="element-1314">
            <Button variant="outline" size="sm" onClick={prevMonth} data-id="element-1315">
              <ChevronLeftIcon className="h-4 w-4" data-id="element-1316" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentMonth(new Date())} data-id="element-1317">
              Vandaag
            </Button>
            <Button variant="outline" size="sm" onClick={nextMonth} data-id="element-1318">
              <ChevronRightIcon className="h-4 w-4" data-id="element-1319" />
            </Button>
          </div>
          <h2 className="text-lg font-medium text-gray-900" data-id="element-1320">
            {currentMonth.toLocaleDateString('nl-NL', {
            month: 'long',
            year: 'numeric'
          })}
          </h2>
          <div className="flex items-center space-x-2 text-xs text-gray-500" data-id="element-1321">
            <div className="flex items-center" data-id="element-1322">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-1" data-id="element-1323"></span>
              Beschikbaar
            </div>
            <div className="flex items-center" data-id="element-1324">
              <span className="w-2 h-2 bg-red-500 rounded-full mr-1" data-id="element-1325"></span>
              Niet beschikbaar
            </div>
            <div className="flex items-center" data-id="element-1326">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-1" data-id="element-1327"></span>
              Training
            </div>
          </div>
        </div>
        <div className="p-4" data-id="element-1328">
          <div className="grid grid-cols-7 gap-1 mb-1" data-id="element-1329">
            {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map(day => <div key={day} className="text-center font-medium text-gray-700 py-1" data-id="element-1330">
                {day}
              </div>)}
          </div>
          <div className="grid grid-cols-7 gap-1" data-id="element-1331">{renderCalendar()}</div>
        </div>
      </Card>

      {/* Upcoming scheduled trainings */}
      <Card title="Ingeplande Trainingen" data-id="element-1332">
        {availabilityData.scheduledTrainings.length > 0 ? <div className="divide-y divide-gray-200" data-id="element-1333">
            {availabilityData.scheduledTrainings.filter(training => new Date(training.date) >= new Date(new Date().setHours(0, 0, 0, 0))).sort((a, b) => new Date(a.date) - new Date(b.date)).map(training => <div key={training.id} className="py-3 flex justify-between items-center" data-id="element-1334">
                  <div data-id="element-1335">
                    <div className="flex items-center" data-id="element-1336">
                      <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-1337" />
                      <span className="font-medium" data-id="element-1338">
                        {new Date(training.date).toLocaleDateString('nl-NL', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long'
                })}
                      </span>
                    </div>
                    <div className="ml-6 text-sm text-gray-500" data-id="element-1339">
                      {training.startTime} - {training.endTime} |{' '}
                      {training.type} | {training.location}
                    </div>
                  </div>
                  <Badge variant="info" data-id="element-1340">Ingepland</Badge>
                </div>)}
          </div> : <div className="py-6 text-center text-gray-500" data-id="element-1341">
            Geen ingeplande trainingen gevonden
          </div>}
      </Card>

      {/* Preferences modal */}
      {showPreferencesModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1342">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" data-id="element-1343">
            <div className="p-4 border-b border-gray-200" data-id="element-1344">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1345">
                Beschikbaarheidsvoorkeuren
              </h3>
            </div>
            <div className="p-4 space-y-4" data-id="element-1346">
              <div data-id="element-1347">
                <label htmlFor="maxDays" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1348">
                  Maximum aantal dagen per week
                </label>
                <select id="maxDays" className="w-full p-2 border border-gray-300 rounded-md" value={editingPreferences.maxDaysPerWeek} onChange={e => setEditingPreferences({
              ...editingPreferences,
              maxDaysPerWeek: parseInt(e.target.value)
            })} data-id="element-1349">
                  {[1, 2, 3, 4, 5, 6, 7].map(num => <option key={num} value={num} data-id="element-1350">
                      {num} {num === 1 ? 'dag' : 'dagen'}
                    </option>)}
                </select>
              </div>
              <div data-id="element-1351">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1352">
                  Voorkeur locaties
                </label>
                <div className="space-y-2" data-id="element-1353">
                  {['Rotterdam', 'Utrecht', 'Amsterdam', 'Eindhoven'].map(location => <div key={location} className="flex items-center" data-id="element-1354">
                        <input type="checkbox" id={`location-${location}`} checked={editingPreferences.preferredLocations.includes(location)} onChange={e => {
                  if (e.target.checked) {
                    setEditingPreferences({
                      ...editingPreferences,
                      preferredLocations: [...editingPreferences.preferredLocations, location]
                    });
                  } else {
                    setEditingPreferences({
                      ...editingPreferences,
                      preferredLocations: editingPreferences.preferredLocations.filter(l => l !== location)
                    });
                  }
                }} className="h-4 w-4 text-[#B61C1C] focus:ring-[#B61C1C] border-gray-300 rounded" data-id="element-1355" />
                        <label htmlFor={`location-${location}`} className="ml-2 text-sm text-gray-700" data-id="element-1356">
                          {location}
                        </label>
                      </div>)}
                </div>
              </div>
              <div data-id="element-1357">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1358">
                  Voorkeur trainingstypes
                </label>
                <div className="space-y-2" data-id="element-1359">
                  {['Sliptraining Basis', 'Sliptraining Gevorderd', 'Defensief Rijden', 'Code 95 Training', 'Calamiteitentraining'].map(type => <div key={type} className="flex items-center" data-id="element-1360">
                      <input type="checkbox" id={`type-${type}`} checked={editingPreferences.preferredTrainingTypes.includes(type)} onChange={e => {
                  if (e.target.checked) {
                    setEditingPreferences({
                      ...editingPreferences,
                      preferredTrainingTypes: [...editingPreferences.preferredTrainingTypes, type]
                    });
                  } else {
                    setEditingPreferences({
                      ...editingPreferences,
                      preferredTrainingTypes: editingPreferences.preferredTrainingTypes.filter(t => t !== type)
                    });
                  }
                }} className="h-4 w-4 text-[#B61C1C] focus:ring-[#B61C1C] border-gray-300 rounded" data-id="element-1361" />
                      <label htmlFor={`type-${type}`} className="ml-2 text-sm text-gray-700" data-id="element-1362">
                        {type}
                      </label>
                    </div>)}
                </div>
              </div>
              <div data-id="element-1363">
                <label className="flex items-center" data-id="element-1364">
                  <input type="checkbox" checked={editingPreferences.preferNotMonday} onChange={e => setEditingPreferences({
                ...editingPreferences,
                preferNotMonday: e.target.checked
              })} className="h-4 w-4 text-[#B61C1C] focus:ring-[#B61C1C] border-gray-300 rounded" data-id="element-1365" />
                  <span className="ml-2 text-sm text-gray-700" data-id="element-1366">
                    Liever niet op maandag
                  </span>
                </label>
              </div>
            </div>
            <div className="flex justify-end space-x-2 p-4 border-t border-gray-200 bg-gray-50" data-id="element-1367">
              <Button variant="outline" onClick={() => setShowPreferencesModal(false)} data-id="element-1368">
                Annuleren
              </Button>
              <Button variant="primary" onClick={handleSavePreferences} data-id="element-1369">
                Opslaan
              </Button>
            </div>
          </div>
        </div>}

      {/* Date modal */}
      {showDateModal && editingDateAvailability && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1370">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" data-id="element-1371">
            <div className="p-4 border-b border-gray-200" data-id="element-1372">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1373">
                Beschikbaarheid{' '}
                {selectedDate.toLocaleDateString('nl-NL', {
              weekday: 'long',
              day: 'numeric',
              month: 'long'
            })}
              </h3>
            </div>
            <div className="p-4 space-y-4" data-id="element-1374">
              <div data-id="element-1375">
                <label className="flex items-center justify-between" data-id="element-1376">
                  <span className="text-sm font-medium text-gray-700" data-id="element-1377">
                    Beschikbaar op deze dag
                  </span>
                  <div className="relative inline-block w-10 mr-2 align-middle select-none" data-id="element-1378">
                    <input type="checkbox" checked={editingDateAvailability.available} onChange={e => setEditingDateAvailability({
                  ...editingDateAvailability,
                  available: e.target.checked
                })} className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" data-id="element-1379" />
                    <label className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${editingDateAvailability.available ? 'bg-green-400' : 'bg-gray-300'}`} data-id="element-1380"></label>
                  </div>
                </label>
              </div>
              {editingDateAvailability.available && <div className="space-y-2" data-id="element-1381">
                  <p className="text-sm font-medium text-gray-700" data-id="element-1382">
                    Beschikbaar in:
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-1383">
                    <Button variant={editingDateAvailability.morningAvailable ? 'primary' : 'outline'} size="sm" onClick={() => setEditingDateAvailability({
                ...editingDateAvailability,
                morningAvailable: !editingDateAvailability.morningAvailable
              })} data-id="element-1384">
                      Ochtend (8:00 - 12:00)
                    </Button>
                    <Button variant={editingDateAvailability.afternoonAvailable ? 'primary' : 'outline'} size="sm" onClick={() => setEditingDateAvailability({
                ...editingDateAvailability,
                afternoonAvailable: !editingDateAvailability.afternoonAvailable
              })} data-id="element-1385">
                      Middag (12:00 - 17:00)
                    </Button>
                    <Button variant={editingDateAvailability.eveningAvailable ? 'primary' : 'outline'} size="sm" onClick={() => setEditingDateAvailability({
                ...editingDateAvailability,
                eveningAvailable: !editingDateAvailability.eveningAvailable
              })} data-id="element-1386">
                      Avond (17:00 - 21:00)
                    </Button>
                  </div>
                </div>}
              <div data-id="element-1387">
                <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1388">
                  Notitie (optioneel)
                </label>
                <input type="text" id="note" className="w-full p-2 border border-gray-300 rounded-md" placeholder={editingDateAvailability.available ? "Bijv. 'Alleen in regio Utrecht'" : "Bijv. 'Vakantie'"} value={editingDateAvailability.note} onChange={e => setEditingDateAvailability({
              ...editingDateAvailability,
              note: e.target.value
            })} data-id="element-1389" />
              </div>
              {/* Show scheduled trainings if any */}
              {hasScheduledTraining(selectedDate) && <div data-id="element-1390">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1391">
                    Ingeplande trainingen:
                  </h4>
                  <div className="space-y-2" data-id="element-1392">
                    {getTrainingsForDate(selectedDate).map(training => <div key={training.id} className="p-2 bg-blue-50 border border-blue-200 rounded-md" data-id="element-1393">
                        <div className="text-sm font-medium" data-id="element-1394">
                          {training.type}
                        </div>
                        <div className="text-xs text-gray-600" data-id="element-1395">
                          {training.startTime} - {training.endTime} |{' '}
                          {training.location}
                        </div>
                      </div>)}
                  </div>
                </div>}
            </div>
            <div className="flex justify-end space-x-2 p-4 border-t border-gray-200 bg-gray-50" data-id="element-1396">
              <Button variant="outline" onClick={() => setShowDateModal(false)} data-id="element-1397">
                Annuleren
              </Button>
              <Button variant="primary" onClick={handleSaveDateAvailability} data-id="element-1398">
                Opslaan
              </Button>
            </div>
          </div>
        </div>}

      <style jsx data-id="element-1399">{`
        .toggle-checkbox:checked {
          right: 0;
          border-color: #10b981;
        }
        .toggle-checkbox:checked + .toggle-label {
          background-color: #10b981;
        }
        .toggle-checkbox {
          right: 0;
          z-index: 1;
          border-color: #d1d5db;
        }
        .toggle-label {
          display: block;
          overflow: hidden;
          cursor: pointer;
          border-radius: 9999px;
          background-color: #d1d5db;
        }
      `}</style>
    </div>;
}